import React, { useState } from "react";
import { API_BASE_URL } from "../config";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, X } from "lucide-react";

const SignInPage = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDemoInfo, setShowDemoInfo] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/login`,
        formData
      );
      const token = response.data.token;
      const userId = response.data.userId;

      localStorage.setItem("token", token);
      if (userId) localStorage.setItem("userId", userId);
      if (response.data.role) {
        localStorage.setItem("role", response.data.role);
      } else {
        console.warn("No role found in response!"); // DEBUG LOG
      }

      navigate("/home");
    } catch (error) {
      // ตรวจสอบว่าเซิร์ฟเวอร์ไม่ตอบสนองหรือไม่
      if (!error.response) {
        // Network error - เซิร์ฟเวอร์ไม่ตอบสนอง
        setError("ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่อหรือลองใหม่อีกครั้ง");
      } else {
        // แสดงข้อความ error จาก backend (รวมถึงข้อความถูกแบน)
        const errorMsg = error.response?.data?.message || error.response?.data || "อีเมลหรือรหัสผ่านไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง";
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Static Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-red-600/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-900/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <Link to="/home" className="inline-flex items-center gap-2 text-3xl font-bold text-white hover:text-red-500 transition-colors">
            <img src="/logo.webp" alt="IT Shop Logo" className="h-10 w-10 object-contain rounded-full" /> IT Shop
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl animate-slide-up">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">ยินดีต้อนรับกลับ</h1>
            <p className="text-white/60">เข้าสู่ระบบเพื่อดำเนินการต่อ</p>
          </div>

          {/* Render Hosting Notice */}
          <div className="mb-6 p-4 bg-amber-500/20 border border-amber-500/30 rounded-xl text-amber-200 text-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-medium mb-1">Portfolio Project</p>
                <p className="text-amber-300/80 text-xs leading-relaxed">
                  เซิร์ฟเวอร์อาจใช้เวลา <strong>50 วินาที - 2 นาที</strong> ในการเริ่มต้น หากไม่มีการใช้งานนาน กรุณารอสักครู่
                </p>
              </div>
            </div>
          </div>

          {/* Demo Info Button */}
          <button
            type="button"
            onClick={() => setShowDemoInfo(true)}
            className="w-full mb-6 py-3 px-4 bg-white/5 border border-white/20 rounded-xl text-white/70 text-sm hover:bg-white/10 transition-all underline"
          >
            ดูข้อมูล Demo Account
          </button>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300 text-sm text-center animate-shake">
              {error}
            </div>
          )}

          {/* Form */}
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <div className="group">
              <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
                อีเมล
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-white/40 group-focus-within:text-red-500 transition-colors" />
                </div>
                <input
                  id="email"
                  type="email"
                  required
                  placeholder="your@email.com"
                  className="block w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Password */}
            <div className="group">
              <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-2">
                รหัสผ่าน
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-white/40 group-focus-within:text-red-500 transition-colors" />
                </div>
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  placeholder="••••••••"
                  className="block w-full pl-12 pr-14 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/40 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-white/60 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-red-500 focus:ring-red-500 focus:ring-offset-0"
                />
                จดจำฉัน
              </label>
              <button
                type="button"
                className="text-red-400 hover:text-red-300 transition-colors bg-transparent border-none p-0 cursor-pointer"
                onClick={() => alert("ระบบลืมรหัสผ่านยังไม่เปิดให้บริการ")}
              >
                ลืมรหัสผ่าน?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-4 px-6 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-black transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  เข้าสู่ระบบ
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-8 flex items-center gap-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/40 text-sm">หรือ</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Sign Up Link */}
          <p className="mt-6 text-center text-white/60">
            ยังไม่มีบัญชี?{" "}
            <Link to="/signup" className="text-red-400 hover:text-red-300 font-medium transition-colors">
              สมัครสมาชิกเลย
            </Link>
          </p>
        </div>

        {/* Footer Text */}
        <p className="mt-8 text-center text-white/30 text-sm animate-fade-in">
          © 2024 IT Shop. สงวนลิขสิทธิ์ทั้งหมด
        </p>
      </div>

      {/* Demo Info Modal - Document Style */}
      {showDemoInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowDemoInfo(false)} />
          <div className="relative bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden">
            {/* Document Header */}
            <div className="bg-gray-100 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Demo Account Information</h2>
              <button
                onClick={() => setShowDemoInfo(false)}
                className="p-1 hover:bg-gray-200 rounded transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Document Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] text-gray-700">
              <div className="mb-6 p-4 border-l-4">
                <p className="text-sm"><strong>รหัสผ่านทุกบัญชี:</strong> <code className="bg-gray-200 px-2 py-1 rounded font-mono">demo123</code></p>
              </div>

              <table className="w-full text-sm border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Role</th>
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Email</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium">Admin</td>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-gray-600">admin@itshop.com</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium" rowSpan="2">Seller</td>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-gray-600">seller@itshop.com</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-gray-600">gaming@itshop.com</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-medium" rowSpan="2">User</td>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-gray-600">buyer@itshop.com</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-2 font-mono text-gray-600">vip@itshop.com</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Document Footer */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <button
                onClick={() => setShowDemoInfo(false)}
                className="px-6 py-2 bg-gray-800 text-white rounded font-medium hover:bg-gray-700 transition-colors text-sm"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }
        .animate-shake {
          animation: shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default SignInPage;
